import React, { useMemo, forwardRef } from 'react';
import { StyleSheet, ActivityIndicator, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import PropTypes from 'prop-types';
import useResponsiveness from '@/hooks/useResponsiveness';
import { Image } from '@/components/Image';
import { WARNING_COLORS, isDarkTheme } from '@/constants/Colors';
import { BORDER_RADIUS } from '@/constants/designTokens';
import Presser from '@/components/Buttons/Presser';
import Tooltip from '@/components/Tooltip';

/** Frosted fill behind the `nav*` pills. Native has no backdrop-filter. */
const NAV_GLASS_FILTER = Platform.OS === 'web'
  ? {
    backdropFilter: 'blur(20px) saturate(1.85)',
    WebkitBackdropFilter: 'blur(20px) saturate(1.85)',
  }
  : {};

const createStyles = (isXS) => StyleSheet.create({
  container: {
    position: 'relative',
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 8,
    transition: 'transform 0.2s ease, background-color 0.2s ease',
  },
  fullWidth: {
    // width: '100%',
    flex: 1,
  },
  round: {
    borderRadius: BORDER_RADIUS.round,
  },
  // Size variants
  extraSmall: {
    height: 22,
    minHeight: 22,
    minWidth: 22,
    paddingHorizontal: 6,
  },
  small: {
    height: 32,
    minHeight: 32,
    minWidth: 32,
    paddingHorizontal: 10,
  },
  medium: {
    height: 40,
    minHeight: 40,
    minWidth: 40,
    paddingHorizontal: 14,
  },
  large: {
    height: isXS ? 48 : 44,
    minHeight: isXS ? 48 : 44,
    minWidth: isXS ? 48 : 44,
    paddingHorizontal: 18,
  },
  // Icon-only sizes
  iconOnlyExtraSmall: {
    width: 22,
    height: 22,
    padding: 0,
  },
  iconOnlySmall: {
    width: 32,
    height: 32,
    padding: 0,
  },
  iconOnlyMedium: {
    width: 40,
    height: 40,
    padding: 0,
  },
  iconOnlyLarge: {
    width: 48,
    height: 48,
    padding: 0,
  },
  // Text styles
  text: {
    textAlign: 'center',
  },
  textExtraSmall: {
    fontSize: isXS ? 13 : 12,
  },
  textSmall: {
    fontSize: isXS ? 14 : 13,
  },
  textMedium: {
    fontSize: isXS ? 15 : 14,
  },
  textLarge: {
    fontSize: isXS ? 16 : 15,
  },
  // Icon styles
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Image styles
  image: {
    borderRadius: BORDER_RADIUS.round,
  },
  imageExtraSmall: {
    width: 14,
    height: 14,
  },
  imageSmall: {
    width: 16,
    height: 16,
  },
  imageMedium: {
    width: 18,
    height: 18,
  },
  imageLarge: {
    width: 20,
    height: 20,
  },
});

const Button = forwardRef(({
  // Content
  children,
  text,
  textWeight = 'normal',
  prependIcon,
  prependImage,
  prependImageSize,
  appendIcon,
  appendIconSize,
  // Appearance
  variant = 'primary',
  size = 'medium',
  isRound = false,
  isFullWidth = false,
  isHollow = false,
  iconIsSolid = false,
  iconColor,
  hoverBackgroundColor,
  isBlurBackground = false,
  // State
  isLoading = false,
  isDisabled = false,
  // Event handlers
  onPress,
  onMouseEnter,
  onMouseLeave,
  accessibilityRole = 'button',
  accessibilityLabel,
  // On iOS/Android, fire press immediately on touch (useful for buttons near keyboard)
  useImmediatePress = false,
  // Additional styles
  style,
  textStyle,
  // Tooltip
  tooltip,
  tooltipShortcut,
  ...props
}, ref) => {
  const { colors, theme } = useTheme();
  const dark = isDarkTheme(theme);
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(isXS), [isXS]);

  // Determine if this is an icon-only button
  const isIconOnly = !text && !children && ((prependIcon || prependImage) || appendIcon) && !((prependIcon || prependImage) && appendIcon);

  // Get size-specific styles
  const getSizeStyles = () => {
    if (isIconOnly) {
      switch (size) {
        case 'extraSmall': return styles.iconOnlyExtraSmall;
        case 'small': return styles.iconOnlySmall;
        case 'large': return styles.iconOnlyLarge;
        default: return styles.iconOnlyMedium;
      }
    }
    switch (size) {
      case 'extraSmall': return styles.extraSmall;
      case 'small': return styles.small;
      case 'large': return styles.large;
      default: return styles.medium;
    }
  };

  // Get variant-specific styles
  const getVariantStyles = ({ pressed, hovered }) => {
    const baseStyles = {
      opacity: (pressed || isDisabled) ? 0.8 : 1,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
    };

    // If custom hover background color is provided and element is hovered
    if (hoverBackgroundColor && hovered) {
      return {
        ...baseStyles,
        backgroundColor: hoverBackgroundColor,
      };
    }

    if (isHollow) {
      switch (variant) {
        case 'primary':
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: colors.accent_color_primary,
          };
        case 'success':
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: colors.color_success,
          };
        case 'purple':
        case 'plus':
        case 'contrast':
          // Legacy names share the neutral brand treatment.
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: dark ? '#FFFFFF' : '#040404',
          };
        case 'secondary':
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: colors.text_color_secondary,
          };
        case 'tertiary':
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: colors.text_color_tertiary,
          };
        case 'accent':
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: colors.accent_color_primary,
          };
        case 'danger':
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: colors.color_destructive,
          };
        case 'warning':
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            borderWidth: 1,
            borderColor: WARNING_COLORS.base,
          };
        case 'transparent':
          return {
            ...baseStyles,
            backgroundColor: hovered ? colors.solid_color_secondary : 'transparent',
          };
        case 'dangerOnHover':
          return {
            ...baseStyles,
            backgroundColor: hovered ? colors.color_destructive : 'transparent',
          };
        default:
          return baseStyles;
      }
    }

    switch (variant) {
      case 'purple':
      case 'plus':
      case 'contrast':
        return {
          ...baseStyles,
          backgroundColor: isDisabled
            ? colors.solid_color_tertiary
            : dark ? (hovered ? '#E5E5E5' : '#FFFFFF') : (hovered ? '#383838' : '#040404'),
        };
      case 'primary':
        return {
          ...baseStyles,
          backgroundColor: hovered ? `${colors.accent_color_primary}dd` : colors.accent_color_primary,
        };
      case 'success':
        return {
          ...baseStyles,
          backgroundColor: hovered ? `${colors.color_success}dd` : colors.color_success,
        };
      case 'secondary':
        return {
          ...baseStyles,
          backgroundColor: 
            hovered ? 
              isBlurBackground ? `${colors.solid_color_secondary_dark}aa` : colors.solid_color_secondary_dark : 
              isBlurBackground ? `${colors.solid_color_secondary}aa` : colors.solid_color_secondary,
          borderWidth: 1,
          borderColor: colors.border_color_primary,
        };
      case 'accent':
        return {
          ...baseStyles,
          backgroundColor: colors.accent_color_primary,
          opacity: hovered ? 0.95 : 1,
        };
      case 'danger':
        return {
          ...baseStyles,
          backgroundColor: hovered ? `${colors.color_destructive}dd` : colors.color_destructive,
        };
      case 'warning':
        return {
          ...baseStyles,
          backgroundColor: hovered ? WARNING_COLORS.hover : WARNING_COLORS.base,
        };
      case 'tertiary':
        return {
          ...baseStyles,
          backgroundColor: hovered ? colors.solid_color_secondary : colors.solid_color_tertiary,
        };
      case 'transparent':
        return {
          ...baseStyles,
          backgroundColor: hovered ? colors.solid_color_secondary : 'transparent',
        };
      case 'nav':
        return {
          ...baseStyles,
          backgroundColor: hovered ? `${colors.solid_color_tertiary}c8` : 'transparent',
          borderWidth: 0,
          ...(hovered ? NAV_GLASS_FILTER : {}),
        };
      // A menu trigger keeps the glass fill while its panel is open: the panel
      // is portaled to <body>, so moving into it drops `hovered` and the
      // trigger would otherwise go fully transparent under its own dropdown.
      case 'navOpen':
        return {
          ...baseStyles,
          backgroundColor: `${colors.solid_color_tertiary}c8`,
          borderWidth: 0,
          ...NAV_GLASS_FILTER,
        };
      case 'navActive':
        return {
          ...baseStyles,
          backgroundColor: colors.solid_color_secondary,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.text_color_tertiary,
        };
      case 'dangerOnHover':
        return {
          ...baseStyles,
          backgroundColor: hovered ? colors.color_destructive : 'transparent',
        };
      default:
        return baseStyles;
    }
  };

  // Get text color based on variant
  const getTextColor = ({ hovered } = {}) => {
    if (isHollow) {
      switch (variant) {
        case 'primary':
        case 'accent':
          return colors.accent_color_primary;
        case 'success':
          return colors.color_success;
        case 'danger':
          return colors.color_destructive;
        case 'warning':
          return WARNING_COLORS.base;
        case 'secondary':
        case 'transparent':
        case 'tertiary':
          return colors.text_color_primary;
        case 'purple':
        case 'plus':
        case 'contrast':
          return dark ? '#FFFFFF' : '#040404';
        case 'dangerOnHover':
          return hovered ? '#FFFFFF' : colors.color_destructive;
        default:
          return colors.text_color_primary;
      }
    }

    switch (variant) {
      case 'purple':
      case 'plus':
      case 'contrast':
        if (isDisabled) return colors.text_color_tertiary;
        return dark ? '#040404' : '#FFFFFF';
      case 'accent':
      case 'primary':
        return colors.accent_text_color;
      case 'success':
      case 'danger':
        return '#FFFFFF';
      case 'warning':
        return WARNING_COLORS.textColor;
      case 'secondary':
      case 'transparent':
      case 'tertiary':
      case 'nav':
      case 'navOpen':
      case 'navActive':
        return colors.text_color_primary;
      case 'dangerOnHover':
        return hovered ? '#FFFFFF' : colors.color_destructive;
      default:
        return '#FFFFFF';
    }
  };

  // Get icon size based on button size
  const getIconSize = () => {
    const plusSize = isXS ? 2 : 0;
    switch (size) {
      case 'extraSmall': return 14;
      case 'small': return 16;
      case 'medium': return 16 + plusSize;
      case 'large': return 16 + plusSize;
      default: return 16 + plusSize;
    }
  };

  // Get image size styles based on button size
  const getImageSize = () => {
    switch (size) {
      case 'extraSmall': return styles.imageExtraSmall;
      case 'small': return styles.imageSmall;
      case 'large': return styles.imageLarge;
      default: return styles.imageMedium;
    }
  };

  const renderIcon = (iconName, position = 'prepend', forceSize = null, { hovered } = {}) => {
    if (!iconName) return null;
    
    return (
      <View style={styles.icon}>
        {React.isValidElement(iconName) ? iconName : <Icon
          name={iconName}
          size={forceSize || getIconSize()}
          color={iconColor || getTextColor({ hovered })}
          solid={iconIsSolid || undefined}
        />}
      </View>
    );
  };

  const renderImage = () => {
    if (!prependImage) return null;

    const imageSize = !!prependImageSize ? {width: prependImageSize, height: prependImageSize} : getImageSize();

    return (
      <Image
        source={{ uri: prependImage }}
        style={[styles.image, imageSize]}
        contentFit="cover"
      />
    );
  };

  const eventsHandlers = {};
  if (onPress) eventsHandlers.onPress = onPress;
  if (onMouseEnter) eventsHandlers.onMouseEnter = onMouseEnter;
  if (onMouseLeave) eventsHandlers.onMouseLeave = onMouseLeave;

  const pressableContent = (
    <Presser
      ref={ref}
      style={({ pressed, hovered }) => [
        styles.button,
        getSizeStyles(),
        getVariantStyles({ pressed, hovered }),
        isRound && styles.round,
        isFullWidth && styles.fullWidth,
        isBlurBackground && { backdropFilter: 'blur(18px) saturate(1.8)', WebkitBackdropFilter: 'blur(18px) saturate(1.8)' },
        style,
      ]}
      disabled={isDisabled || isLoading}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel ?? (Platform.OS === 'macos' && typeof text === 'string' ? text : undefined)}
      useImmediatePress={useImmediatePress}
      {...eventsHandlers}
      {...props}
    >
      {({ hovered }) => (
        isLoading ? (
          <ActivityIndicator size="small" color={getTextColor({ hovered })} />
        ) : (
          <>
            {renderImage()}
            {renderIcon(prependIcon, 'prepend', null, { hovered })}
            {(text || children) && (
              <Text
                style={[
                  styles.text,
                  size === 'extraSmall' && styles.textExtraSmall,
                  size === 'small' && styles.textSmall,
                  size === 'medium' && styles.textMedium,
                  size === 'large' && styles.textLarge,
                  { color: getTextColor({ hovered }) },
                  textStyle,
                ]}
                numberOfLines={1}
                weight={textWeight}
              >
                {text || children}
              </Text>
            )}
            {renderIcon(appendIcon, 'append', appendIconSize, { hovered })}
          </>
        )
      )}
    </Presser>
  );

  return (
    <View style={[
      styles.container,
      isFullWidth && styles.fullWidth,
      // Native flex layout must reserve the button's visible height even
      // when its full-width wrapper is measured inside an auto-height row.
      Platform.OS === 'macos' && { minHeight: getSizeStyles().minHeight },
    ]}>
      {tooltip ? (
        <Tooltip label={tooltip} keyboardShortcut={tooltipShortcut}>
          {pressableContent}
        </Tooltip>
      ) : pressableContent}
    </View>
  );
});

Button.propTypes = {
  children: PropTypes.node,
  text: PropTypes.string,
  prependIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  prependImage: PropTypes.string,
  prependImageSize: PropTypes.string,
  appendIcon: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'contrast', 'secondary', 'accent', 'plus', 'purple', 'danger', 'warning', 'success', 'tertiary', 'transparent', 'dangerOnHover', 'nav', 'navOpen', 'navActive']),
  size: PropTypes.oneOf(['extraSmall', 'small', 'medium', 'large']),
  isRound: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isHollow: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  iconIsSolid: PropTypes.bool,
  iconColor: PropTypes.string,
  hoverBackgroundColor: PropTypes.string,
  isBlurBackground: PropTypes.bool,
  onPress: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  accessibilityRole: PropTypes.string,
  useImmediatePress: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  textStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  textWeight: PropTypes.string,
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

Button.displayName = 'Button';

export default React.memo(Button);
